import React, { useState, useRef, useEffect } from 'react';
import { FaPlay, FaPause, FaDownload } from 'react-icons/fa';

interface AudioPlayerProps {
  src: string | null;
  title?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, title }) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressBarRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (src && audioRef.current) {
      const audio = audioRef.current;
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('ended', handleEnded);

      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('ended', handleEnded);
      };
    } else {
      setIsPlaying(false);
      setDuration(0);
      setCurrentTime(0);
    }
  }, [src]);

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play();
        setIsPlaying(true);
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      const time = Number(e.target.value);
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleDownload = async () => {
    if (!src) return;
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = title || 'audio-file.mp3';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const isDisabled = !src;

  return (
    <div className="flex items-center space-x-3 p-2">
      <button
        onClick={togglePlayPause}
        disabled={isDisabled}
        className={`w-10 h-10 flex items-center justify-center rounded-full focus:outline-none ${
          isDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
      >
        {isPlaying ? <FaPause size={16} /> : <FaPlay size={16} className="ml-1" />}
      </button>

      <div className="flex-grow">
        <input
          ref={progressBarRef}
          type="range"
          value={currentTime}
          max={duration}
          onChange={handleProgressChange}
          disabled={isDisabled}
          className={`w-full h-1.5 rounded-full appearance-none cursor-pointer ${
            isDisabled ? 'bg-gray-100' : 'bg-gray-200'
          }`}
        />
      </div>

      <div className="text-sm text-gray-500 w-16 text-right">
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>

      <button onClick={handleDownload} disabled={isDisabled} className={`text-gray-400 ${isDisabled ? 'cursor-not-allowed' : 'hover:text-gray-600'}`}>
        <FaDownload size={14} />
      </button>

      {src && <audio ref={audioRef} src={src} />}
    </div>
  );
};

export default AudioPlayer;
